import { z } from 'zod'

import { compareCivilDate } from '~/models/civilDate'

/**
 * Zod `.max(N)` は UTF-16 code unit 数で判定するが、proto CEL `string.max_len`
 * と Go `utf8.RuneCountInString` は Unicode code point (rune) 数で判定する。
 * 補助 plane 文字 (𠮷 U+20BB7 等の姓、emoji 等) は UTF-16 で 2 units として数えられ
 * 3 層一致が破れる (AC-03)。`.refine(withinRunes(N), msg)` で rune 数判定に揃える。
 */
export const withinRunes = (n: number) => (v: string) =>
  Array.from(v).length <= n

/**
 * google.type.Date 形式 ({year, month, day}) の zod schema。
 * proto の google.type.Date を RHF で扱うためのシリアライズ形式。
 */
export const civilDateSchema = z.object({
  year: z.number().int().min(2000),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
})

export type CivilDateInput = z.infer<typeof civilDateSchema>

/**
 * 「適用終了日 ≥ 適用開始日（同日許容）」の cross-field 検証 (backend entity の
 * validateValidPeriod と同じ境界、occto-master-valid-period-6tabs 設計書 §5.4)。
 *
 * 7 タブの form schema (`validFrom: civilDateSchema`, `validTo:
 * civilDateSchema.optional()` を持つ object) は
 * `.refine(validPeriodRefine, validPeriodRefineIssue)` でこれを共有する
 * (= 個別に refine ロジックを再実装しない)。
 */
export function validPeriodRefine(v: {
  validFrom: CivilDateInput
  validTo?: CivilDateInput | undefined
}): boolean {
  if (v.validTo === undefined) return true
  const { validFrom, validTo } = v
  if (validTo.year !== validFrom.year) return validTo.year > validFrom.year
  if (validTo.month !== validFrom.month) return validTo.month > validFrom.month
  return validTo.day >= validFrom.day
}

/** validPeriodRefine と対にする zod issue params (message / path 固定)。 */
export const validPeriodRefineIssue: { message: string; path: string[] } = {
  message: '適用終了日は適用開始日以降の日付で入力してください',
  path: ['validTo'],
}

export function planMasterRowValidFromRefine(
  recordValidFrom: CivilDateInput,
  rows: Array<{ validFrom: CivilDateInput }>,
  message: string,
  path: string,
  ctx: z.RefinementCtx,
): void {
  for (const [index, row] of rows.entries()) {
    if (compareCivilDate(row.validFrom, recordValidFrom) < 0) {
      ctx.addIssue({
        code: 'custom',
        message,
        path: [path, index, 'validFrom'],
      })
    }
  }
}

/**
 * 取引相手コード (= codes[] の 1 エントリ) 入力 schema。
 */
export const planMasterCodeInputSchema = z.object({
  /** OCCTO 計画提出コード */
  code: z.string().min(1, '取引先を選択してください'),
  /**
   * 取引先コード名称 (表示名)。 proto CEL `max_len 150` (PlanMasterCodeInput.code_name)
   * と揃える (AC-04、3938-occto-master-plan-spec-followup 設計書 §5)。
   */
  codeName: z
    .string()
    .max(150, '取引先コード名称は150文字以内で入力してください'),
  /** 自社コード区分 */
  isInternal: z.boolean(),
  /** 適用開始日 */
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化して、
  // 以降の validPeriodRefine・送信は従来どおり `undefined = 無期限` で扱う。
  // `.optional()` を末尾に付けて object key の optional 性 (省略可) を保つ。
  /** 適用終了日 (任意) */
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type PlanMasterCodeInputValues = z.infer<
  typeof planMasterCodeInputSchema
>

/**
 * 登録小売電気事業者 登録番号の形式: 「A」+ 半角数字 1 桁以上 (桁数固定なし)。
 * backend entity (validateRegistrationNumber, Go は `^A\d+$`) / proto CEL
 * (`string.pattern = "^A[0-9]+$"`) と等価な正規表現 (`\d` ≡ `[0-9]`。
 * spec AC-OMU-012、occto-master-registration-number 設計書 Decision #5)。
 * BusinessMaster / TradingPartner で共有。
 */
export const REGISTRATION_NUMBER_FORMAT = /^A\d+$/

/** spec AC-OMU-012 / AC-OMT-027 の文言 verbatim。 */
export const REGISTRATION_NUMBER_FORMAT_MESSAGE =
  '登録番号は「A」に続けて数字で入力してください (例: A0001)'

/**
 * registrationNumber (任意項目) の Zod schema。空文字は「未入力」として
 * 許容し、 refine では検証しない (= 3 層一致は非空時のみ、Decision #5)。
 * 上限 20 rune は mock maxlength=20 を踏襲 (proto CEL `string.max_len = 20`
 * と同期)。BusinessMaster / TradingPartner の両タブが共有する (Decision #6)。
 */
export const registrationNumberSchema = z
  .string()
  .refine(
    (v) => v === '' || withinRunes(20)(v),
    '登録番号は20文字以内で入力してください',
  )
  .refine(
    (v) => v === '' || REGISTRATION_NUMBER_FORMAT.test(v),
    REGISTRATION_NUMBER_FORMAT_MESSAGE,
  )

/**
 * 所属 BG (= bgs[] の 1 エントリ) 入力 schema。
 */
export const planMasterBgInputSchema = z.object({
  /** BG マスタ UUID */
  bgMasterId: z.string().min(1, 'BG を選択してください'),
  /** 適用開始日 */
  validFrom: civilDateSchema,
  // クリア時は RHF field-array の制約で `null` が入る (PeriodFields の
  // clearAsNull JSDoc 参照)。`null` を許容し `undefined` へ正規化する。
  /** 適用終了日 (任意) */
  validTo: civilDateSchema
    .nullable()
    .transform((v) => v ?? undefined)
    .optional(),
})

export type PlanMasterBgInputValues = z.infer<typeof planMasterBgInputSchema>

/**
 * 所属 BG (= bgs[] の 1 エントリ) 入力 schema — 需要調達計画マスタ専用。
 *
 * DemandProcurementMasterBg は行ごとの期間を持たない (親 = 需要調達計画マスタの
 * valid_from/valid_to に従う、occto-master-valid-period-6tabs 設計書 §5.2/AC-12)。
 * GenerationSalesMaster の所属 BG (= planMasterBgInputSchema、行ごとに期間を持つ)
 * とは意図的に別 schema にしている。
 */
export const demandProcurementBgInputSchema = z.object({
  /** BG マスタ UUID */
  bgMasterId: z.string().min(1, 'BG を選択してください'),
})

export type DemandProcurementBgInputValues = z.infer<
  typeof demandProcurementBgInputSchema
>
